import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCamera, FiCalendar, FiUpload, FiCheck, FiChevronRight, FiChevronLeft, FiZap } from 'react-icons/fi';
import {
  Eye, Users, Lightbulb, Maximize, ArrowRight, CheckCircle,
  RefreshCw, ShieldCheck, ScanLine, Sparkles, User, Mail,
  Phone, Globe, Star
} from 'lucide-react';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useCountry } from '../../contexts/CountryContext';
import { FaWhatsapp } from 'react-icons/fa';
import Webcam from 'react-webcam';
import languages from '../../config/languages';
import i18n from '../../i18n';

// Onboarding images
import onboardingHelper from '../../assets/images/step01.png';
import onboardingLighting from '../../assets/images/step02.png';
import onboardingDistance from '../../assets/images/onboarding-distance.png';

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const WIZARD_STEPS = [
  { id: 'onboarding', label: 'Orientações', icon: Eye },
  { id: 'capture', label: 'Captura', icon: ScanLine },
  { id: 'validate', label: 'Validação', icon: ShieldCheck },
  { id: 'personal', label: 'Confirmação', icon: User },
];

const ONBOARDING_SLIDES = [
  {
    id: 1,
    icon: Users,
    title: 'Peça ajuda para tirar a foto',
    subtitle: 'A melhor qualidade vem da colaboração',
    description: 'A qualidade ideal da imagem é obtida quando outra pessoa segura a câmera apontada para o seu olho. Isso elimina tremores e garante foco perfeito.',
    tips: [
      { ok: true, text: 'Peça a um amigo ou familiar para segurar o celular' },
      { ok: true, text: 'Abra bem seu olho e fique imóvel olhando para a câmera' },
      { ok: false, text: 'Evite tirar a foto sozinho no espelho' },
    ],
    image: onboardingHelper,
    accent: '#09c4a9',
  },
  {
    id: 2,
    icon: Lightbulb,
    title: 'Iluminação correta é essencial',
    subtitle: 'A luz define a qualidade da análise',
    description: 'A luz define a qualidade da análise. Use uma lanterna auxiliar ao LADO DO OLHO para iluminar a íris.',
    tips: [
      { ok: true, text: 'Pode usar a lanterna de OUTRO celular ou outra fonte de luz' },
      { ok: true, text: 'Posicione a luz ao lado do olho.' },
      //{ ok: false, text: 'Não use o flash direto no olho' },
      { ok: false, text: 'Evite reflexos ou sombras na íris' },
    ],
    image: onboardingLighting,
    accent: '#09c4a9',
  },
  {
    id: 3,
    icon: Maximize,
    title: 'Distância e foco',
    subtitle: 'Aproxime até a íris preencher o guia',
    description: 'Posicione a câmera a aproximadamente 10-12 cm do olho. A íris deve preencher o círculo guia na tela sem perder o foco.',
    tips: [
      { ok: true, text: 'Abra bem o seu olho (levante a sobrancelha), pode usar os dois dedos (polegar e indicador) para abrir o olho' },
      { ok: true, text: 'Fixe o seu olhar diretamente na câmera.' },
      { ok: false, text: 'Não aproxime demais, a imagem não pode ficar borrada' },
    ],
    image: onboardingDistance,
    accent: '#09c4a9',
  },
];

const PERSONAL_STEPS = [
  { field: 'name', label: 'Qual é o seu nome?', placeholder: 'Seu nome completo', icon: User, type: 'text', inputMode: 'text' },
  { field: 'email', label: 'Qual é o seu melhor e-mail?', placeholder: 'seu@email.com', icon: Mail, type: 'email', inputMode: 'email' },
  { field: 'phone', label: 'Qual é o seu telefone?', placeholder: '(00) 00000-0000', icon: Phone, type: 'tel', inputMode: 'tel', masked: true },
  { field: 'birthDate', label: 'Qual é a sua data de nascimento?', placeholder: 'DD/MM/AAAA', icon: FiCalendar, type: 'text', inputMode: 'numeric', masked: true, dateMask: true },
  //{ field: 'language', label: 'Em qual idioma deseja receber a análise?', placeholder: null, icon: Globe, type: 'select' },
];

// ─────────────────────────────────────────────────────────────
// QUALITY CHECK HELPERS (Laplacian variance approximation via Canvas)
// ─────────────────────────────────────────────────────────────
const computeBlurScore = (imageSrc) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const MAX = 200;
      const scale = Math.min(MAX / img.width, MAX / img.height);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

      let totalLap = 0;
      let totalGray = 0;
      let count = 0;

      // Grayscale (Luminance weights) + Laplacian kernel
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const idx = (y * width + x) * 4;
          // Standard luminance weights for more accurate grayscale
          const gray = (data[idx] * 77 + data[idx + 1] * 150 + data[idx + 2] * 29) >> 8;
          totalGray += gray;

          const top = ((y - 1) * width + x) * 4;
          const bot = ((y + 1) * width + x) * 4;
          const lft = (y * width + (x - 1)) * 4;
          const rgt = (y * width + (x + 1)) * 4;

          const gTop = (data[top] * 77 + data[top + 1] * 150 + data[top + 2] * 29) >> 8;
          const gBot = (data[bot] * 77 + data[bot + 1] * 150 + data[bot + 2] * 29) >> 8;
          const gLft = (data[lft] * 77 + data[lft + 1] * 150 + data[lft + 2] * 29) >> 8;
          const gRgt = (data[rgt] * 77 + data[rgt + 1] * 150 + data[rgt + 2] * 29) >> 8;

          const lap = Math.abs(4 * gray - gTop - gBot - gLft - gRgt);
          totalLap += lap;
          count++;
        }
      }

      const avgGray = totalGray / count;
      const meanLap = totalLap / count;

      // Boost score for darker images as contrast is naturally lower
      // A sharp dark image should still get a good score.
      const brightnessBoost = Math.max(1, Math.min(2.5, 140 / (avgGray + 20)));

      // Base multiplier slightly increased to 4.5 for overall leniency as requested
      const score = Math.min(100, Math.round(meanLap * 4.5 * brightnessBoost));
      resolve(score);
    };
    img.onerror = () => resolve(50);
    img.src = imageSrc;
  });
};

// ─────────────────────────────────────────────────────────────
// CROP IMAGE
// ─────────────────────────────────────────────────────────────
// Center-crop only — zero distortion, zero scaling, max quality.
// Reads the original pixel dimensions, crops the centred square at
// 65 % of the shorter side, and re-encodes at JPEG quality 1.0 (≈ lossless).
const resizeImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const { width, height } = img;
      // 65 % of the shorter side → centre square
      const size = Math.min(width, height) * 0.65;
      const startX = (width - size) / 2;
      const startY = (height - size) / 2;
      canvas.width = size;   // canvas == crop size → 1-to-1 pixel copy
      canvas.height = size;
      // drawImage with identical src & dst sizes = pure crop, no interpolation
      ctx.drawImage(img, startX, startY, size, size, 0, 0, size, size);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          blob ? resolve(blob) : reject(new Error('Erro ao recortar imagem'));
        },
        'image/jpeg',
        1.0   // maximum quality — avoid double-compression artefacts
      );
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Erro ao carregar imagem')); };
    img.src = objectUrl;
  });
};

// ─────────────────────────────────────────────────────────────
// EYE / PUPIL DETECTION — pure Canvas, no external libraries
// Detects whether the image contains an iris/pupil by analysing:
//   1. A dark circular cluster near the centre (pupil)
//   2. A concentric brightness gradient (pupil → iris)
//   3. A scan for any dark circle across the whole frame (off-centre pupils)
// Returns { isValid, score 0-100, details }
// ─────────────────────────────────────────────────────────────
const detectEyeInImage = (imageUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const SZ = 140; // analyse at 140 × 140 px — fast and sufficient
      const canvas = document.createElement('canvas');
      canvas.width = SZ;
      canvas.height = SZ;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, SZ, SZ);
      const { data } = ctx.getImageData(0, 0, SZ, SZ);

      // Grayscale (integer weights, shift right by 8)
      const gray = new Uint8Array(SZ * SZ);
      for (let i = 0; i < SZ * SZ; i++) {
        gray[i] = (data[i * 4] * 77 + data[i * 4 + 1] * 150 + data[i * 4 + 2] * 29) >> 8;
      }

      const cx = SZ / 2, cy = SZ / 2;

      // ── Feature 1: concentric ring brightness from centre ──────────
      // Pupil → innermost ring should be significantly darker
      const sampleRing = (r) => {
        const steps = Math.max(12, Math.round(2 * Math.PI * r / 1.5));
        let sum = 0, n = 0;
        for (let i = 0; i < steps; i++) {
          const a = (i / steps) * Math.PI * 2;
          const px = Math.round(cx + r * Math.cos(a));
          const py = Math.round(cy + r * Math.sin(a));
          if (px >= 0 && px < SZ && py >= 0 && py < SZ) {
            sum += gray[py * SZ + px];
            n++;
          }
        }
        return n > 0 ? sum / n : 128;
      };

      const b = [6, 14, 24, 36, 52].map(sampleRing); // [pupil, iris×3, sclera]

      // Calculate overall average brightness to adjust thresholds
      const avgBrightness = gray.reduce((sum, val) => sum + val, 0) / (SZ * SZ);

      // Adjust thresholds based on lighting conditions
      // In darker images, we allow lower absolute gradients
      const pupilDarkThreshold = Math.min(90, Math.max(50, avgBrightness * 0.9));
      const gradientFloor = Math.max(10, avgBrightness * 0.15);
      const varianceFloor = Math.max(25, avgBrightness * 0.3);

      const pupilDark = b[0] < pupilDarkThreshold;
      const irisGradient = (b[1] + b[2]) / 2 > b[0] + gradientFloor;
      const totalVariance = Math.max(...b) - Math.min(...b);
      const goodVariance = totalVariance > varianceFloor;

      // ── Feature 2: dark pixel cluster in central 12 px radius ──────
      let darkPx = 0, totalPx = 0;
      for (let y = 0; y < SZ; y++) {
        for (let x = 0; x < SZ; x++) {
          if (Math.hypot(x - cx, y - cy) < 14) {
            totalPx++;
            if (gray[y * SZ + x] < 80) darkPx++;
          }
        }
      }
      const hasDarkCentre = darkPx / Math.max(1, totalPx) > 0.30;

      // ── Feature 3: scan full image for ANY dark circular cluster ───
      // Catches off-centre pupils (gallery uploads where eye isn't centred)
      let bestDarkRatio = 0;
      const R = 10, STEP = 7;
      for (let sy = R; sy < SZ - R; sy += STEP) {
        for (let sx = R; sx < SZ - R; sx += STEP) {
          let dark = 0, tot = 0;
          for (let dy = -R; dy <= R; dy++) {
            for (let dx = -R; dx <= R; dx++) {
              if (Math.hypot(dx, dy) <= R) {
                const px = sx + dx, py = sy + dy;
                if (px >= 0 && px < SZ && py >= 0 && py < SZ) {
                  tot++;
                  if (gray[py * SZ + px] < 70) dark++;
                }
              }
            }
          }
          const ratio = dark / Math.max(1, tot);
          if (ratio > bestDarkRatio) bestDarkRatio = ratio;
        }
      }
      const hasCircularDark = bestDarkRatio > 0.50;

      // ── Scoring ─────────────────────────────────────────────────────
      let score = 0;
      if (pupilDark) score += 30;
      if (irisGradient) score += 25;
      if (goodVariance) score += 20;
      if (hasDarkCentre || hasCircularDark) score += 25;

      resolve({
        isValid: score >= 50,
        score,
        details: { pupilDark, irisGradient, goodVariance, hasDarkCircle: hasDarkCentre || hasCircularDark },
      });
    };
    img.onerror = () => resolve({ isValid: false, score: 0, details: {} });
    img.src = imageUrl;
  });
};

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────

// Top Progress Stepper
const WizardStepper = ({ currentStep }) => {
  const stepIdx = WIZARD_STEPS.findIndex(s => s.id === currentStep);
  return (
    <div className="cf-wizard-stepper">
      {WIZARD_STEPS.map((step, i) => {
        const Icon = step.icon;
        const state = i < stepIdx ? 'completed' : i === stepIdx ? 'active' : 'pending';
        return (
          <React.Fragment key={step.id}>
            <div className={`cf-step ${state}`}>
              <div className="cf-step-circle">
                {state === 'completed' ? <FiCheck size={14} /> : <Icon size={14} />}
              </div>
              <span className="cf-step-label">{step.label}</span>
            </div>
            {i < WIZARD_STEPS.length - 1 && (
              <div className={`cf-step-line ${i < stepIdx ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Onboarding Carousel
const OnboardingStep = ({ onComplete }) => {
  const [slide, setSlide] = useState(0);
  const current = ONBOARDING_SLIDES[slide];
  const Icon = current.icon;
  const isLast = slide === ONBOARDING_SLIDES.length - 1;

  return (
    <div className="cf-onboarding">
      {/* Slide indicators */}
      <div className="cf-onboarding-header">
        <div className="cf-onboarding-img-badge" style={{ background: current.accent }}>
          <Icon size={18} color="#fff" />
          <span>Passo {slide + 1} de {ONBOARDING_SLIDES.length}</span>
        </div>
        <div className="cf-onboarding-dots">
          {ONBOARDING_SLIDES.map((_, i) => (
            <button key={i} className={`cf-dot ${i === slide ? 'active' : ''}`} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`} />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={slide}
          className="cf-onboarding-slide"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.35 }}
        >
          {/* Image */}
          <div className="cf-onboarding-img-wrap">
            <img src={current.image} alt={current.title} className="cf-onboarding-img" />
          </div>

          {/* Content */}
          <div className="cf-onboarding-content">
            <h2 className="cf-onboarding-title">{current.title}</h2>
            <p className="cf-onboarding-subtitle">{current.description}</p>

            <div className="cf-onboarding-tips">
              {current.tips.map((tip, i) => (
                <div key={i} className={`cf-tip ${tip.ok ? 'ok' : 'bad'}`}>
                  <span className="cf-tip-icon">{tip.ok ? '✓' : '✗'}</span>
                  <span>{tip.text}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="cf-onboarding-nav">
        {slide > 0 && (
          <button className="cf-btn-ghost" onClick={() => setSlide(s => s - 1)}>
            <FiChevronLeft /> Anterior
          </button>
        )}
        <button
          className="cf-btn-primary"
          onClick={() => isLast ? onComplete() : setSlide(s => s + 1)}
          style={{ marginLeft: 'auto' }}
        >
          {isLast ? (
            <><FiCamera /> Iniciar Captura</>
          ) : (
            <>Próximo <FiChevronRight /></>
          )}
        </button>
      </div>
    </div>
  );
};

// Camera Overlay with quality feedback
const CameraCapture = ({ cameraEye, onCapture, onClose }) => {
  const webcamRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomCapabilities, setZoomCapabilities] = useState({ min: 1, max: 1, step: 0.1 });
  const [qualityScore, setQualityScore] = useState(null);
  const [message, setMessage] = useState('Posicione a íris no círculo');
  const [isCapturing, setIsCapturing] = useState(false);
  const qualityTimerRef = useRef(null);

  const handleUserMedia = (stream) => {
    try {
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();
      /*if (capabilities.zoom) {
        setZoomCapabilities({
          min: capabilities.zoom.min,
          max: capabilities?.zoom?.max > 6 ? 6 : capabilities.zoom.max,
          step: capabilities.zoom.step || 0.1
        });
        const targetZoom = 1;
        const initialZoom = Math.min(Math.max(targetZoom, capabilities.zoom.min), capabilities.zoom.max);
        setZoomLevel(initialZoom);
        track.applyConstraints({ advanced: [{ zoom: initialZoom }] }).catch(() => { });
      }*/
    } catch (e) {
      console.warn('Zoom init error', e);
    }
  };

  const handleZoomChange = async (e) => {
    const newZoom = parseFloat(e.target.value);
    setZoomLevel(newZoom);
    if (webcamRef.current?.stream) {
      const track = webcamRef.current.stream.getVideoTracks()[0];
      try { await track.applyConstraints({ advanced: [{ zoom: newZoom }] }); } catch { }
    }
  };

  // Live quality sampling
  useEffect(() => {
    qualityTimerRef.current = setInterval(async () => {
      if (webcamRef.current) {
        const screenshot = webcamRef.current.getScreenshot({ width: 400, height: 400 });
        if (screenshot) {
          const score = await computeBlurScore(screenshot);
          setQualityScore(score);
          if (score >= 75) setMessage('✦ Excelente! Mantenha estável');
          else if (score >= 50) setMessage('Quase lá — ajuste o foco');
          else setMessage('Aproxime a câmera do olho');
        }
      }
    }, 1200);
    return () => clearInterval(qualityTimerRef.current);
  }, []);

  const takePhoto = useCallback(async () => {
    if (isCapturing) return;
    if (qualityScore !== null && qualityScore < 40) {
      toast.warning('Ajuste a iluminação e/ou a distância do olho para melhor precisão');
      return;
    }
    setIsCapturing(true);
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        try {
          const res = await fetch(imageSrc);
          const blob = await res.blob();
          const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
          onCapture(file);
        } catch (err) {
          toast.error('Erro ao processar captura');
          setIsCapturing(false);
        }
      } else {
        toast.error('Erro ao capturar imagem.');
        setIsCapturing(false);
      }
    }
  }, [webcamRef, qualityScore, onCapture, isCapturing]);

  const scoreColor = qualityScore === null ? '#888' : qualityScore >= 75 ? '#09c4a9' : qualityScore >= 45 ? '#f39c12' : '#e74c3c';

  return (
    <div className="cf-camera-overlay">
      {/* Header */}
      <div className="cf-camera-header">
        <div className="cf-camera-eye-label">
          <Eye size={16} />
          <span>Olho <strong>{cameraEye === 'left' ? 'ESQUERDO' : 'DIREITO'}</strong></span>
        </div>
        <button type="button" onClick={onClose} className="cf-camera-close">
          <FiX size={20} />
        </button>
      </div>

      {/* Webcam */}
      <div className="cf-camera-viewport">
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={{
            facingMode: { ideal: 'environment' },
            frameRate: { ideal: 30 },
            width: { ideal: 4096 },
            height: { ideal: 4096 }
          }}
          className="cf-camera-video"
          onUserMedia={handleUserMedia}
          onUserMediaError={() => toast.error('Erro ao acessar câmera')}
          screenshotQuality={1}
        />

        {/* Iris guide overlay */}
        <div className="cf-iris-guide">
          <div className="cf-iris-ring outer" />
          <div className="cf-iris-ring inner" />
          <div className="cf-iris-crosshair h" />
          <div className="cf-iris-crosshair v" />
        </div>

        {/* Message */}
        <div className="cf-camera-message">{message}</div>

        {/* Quality score */}
        {qualityScore !== null && (
          <div className="cf-quality-badge" style={{ borderColor: scoreColor, color: scoreColor }}>
            <FiZap size={12} />
            <span>Qualidade: <strong>{qualityScore}</strong>/100</span>
          </div>
        )}

        {/* Zoom 
        {zoomCapabilities.max > zoomCapabilities.min && (
          <div className="cf-zoom-overlay">
            <span className="cf-zoom-label">Zoom: {zoomLevel.toFixed(1)}×</span>
            <input
              type="range"
              className="cf-zoom-slider"
              min={Math.max(1, zoomCapabilities.min)}
              max={zoomCapabilities.max}
              step={zoomCapabilities.step}
              value={zoomLevel}
              onChange={handleZoomChange}
            />
          </div>
        )}*/}
      </div>

      {/* Controls */}
      <div className="cf-camera-controls">
        <button type="button" className="cf-capture-btn" onClick={takePhoto} disabled={isCapturing} aria-label="Capturar">
          <div className="cf-capture-inner" />
        </button>
        <span className="cf-capture-hint">Toque para capturar</span>
      </div>
    </div>
  );
};

// Eye Card with capture state
const EyeCard = ({ side, label, image, onCameraOpen, onUpload, onRemove, isLoading }) => {
  const hasImage = !!image;
  const fileInputRef = useRef(null);

  return (
    <motion.div
      className={`cf-eye-card ${hasImage ? 'captured' : ''}`}
      whileHover={{ scale: hasImage ? 1 : 1.02 }}
      transition={{ type: 'spring', stiffness: 300 }}
    >
      {hasImage ? (
        <div className="cf-eye-preview">
          <img src={image.url} alt={label} className="cf-eye-img" />
          <div className="cf-eye-success-badge">
            <CheckCircle size={14} /> Capturado
          </div>
          <button
            type="button"
            className="cf-eye-remove"
            onClick={onRemove}
            disabled={isLoading}
          >
            <RefreshCw size={14} /> Recapturar
          </button>
        </div>
      ) : (
        <div className="cf-eye-empty">
          <div className="cf-eye-icon-wrap">
            <Eye size={32} className="cf-eye-icon" />
          </div>
          <span className="cf-eye-name">{label}</span>
          <p className="cf-eye-hint">Posicione a íris no círculo e capture</p>
          <div className="cf-eye-actions">
            <button
              type="button"
              className="cf-btn-capture"
              onClick={onCameraOpen}
              disabled={isLoading}
            >
              <FiCamera size={16} /> Capturar
            </button>
            <label className="cf-btn-gallery">
              <FiUpload size={14} /> Galeria
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onUpload}
                disabled={isLoading}
              />
            </label>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Personal data wizard (conversational)
const PersonalDataWizard = ({ formData, onChange, onComplete, isSubmitting }) => {
  const { countryConfig } = useCountry();
  const [step, setStep] = useState(0);
  const [value, setValue] = useState('');
  const currentStep = PERSONAL_STEPS[step];
  const progress = Math.round(((step) / PERSONAL_STEPS.length) * 100);
  const inputRef = useRef(null);

  useEffect(() => {
    // Pre-fill from formData
    setValue(formData[currentStep.field] || '');
    setTimeout(() => inputRef.current && inputRef.current.focus(), 100);
  }, [step]);

  const handleNext = () => {
    if (isSubmitting) return;
    
    if (!value.trim() && currentStep.field !== 'language') {
      toast.warning('Por favor, preencha este campo.');
      return;
    }
    if (currentStep.field === 'email' && !/\S+@\S+\.\S+/.test(value)) {
      toast.warning('Por favor, informe um e-mail válido.');
      return;
    }
    if (currentStep.field === 'birthDate' && (!value.trim() || value.length < 10)) {
      toast.warning('Por favor, informe sua data de nascimento no formato DD/MM/AAAA.');
      return;
    }
    
    onChange(currentStep.field, value);
    if (step < PERSONAL_STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      onComplete();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleNext();
  };

  const Icon = currentStep.icon;
  const isLast = step === PERSONAL_STEPS.length - 1;

  return (
    <div className="cf-personal-wizard">
      {/* Progress bar */}
      <div className="cf-personal-progress-bar">
        <div className="cf-personal-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="cf-personal-progress-label">{progress}% concluído</div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          className="cf-personal-step"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <div className="cf-personal-icon">
            <Icon size={24} />
          </div>
          <h2 className="cf-personal-question">{currentStep.label}</h2>

          {currentStep.type === 'select' ? (
            <select
              ref={inputRef}
              className="cf-personal-input"
              value={value || i18n.language}
              onChange={e => setValue(e.target.value)}
            >
              {languages.map((lang, i) => (
                <option key={i} value={lang.code}>{lang.name}</option>
              ))}
            </select>
          ) : currentStep.dateMask ? (
            <InputMask
              inputRef={(el) => { inputRef.current = el; }}
              mask="99/99/9999"
              type="text"
              className="cf-personal-input"
              placeholder={currentStep.placeholder}
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          ) : currentStep.masked ? (
            <InputMask
              inputRef={(el) => { inputRef.current = el; }}
              mask={countryConfig?.phone?.mask || '(99) 99999-9999'}
              className="cf-personal-input"
              placeholder={currentStep.placeholder}
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <input
              ref={inputRef}
              type={currentStep.type}
              inputMode={currentStep.inputMode}
              className="cf-personal-input"
              placeholder={currentStep.placeholder}
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          )}

          <div className="cf-personal-nav">
            {step > 0 && (
              <button
                type="button"
                className="cf-btn-ghost"
                onClick={() => { onChange(currentStep.field, value); setStep(s => s - 1); }}
              >
                <FiChevronLeft /> Voltar
              </button>
            )}
            <button
              type="button"
              className="cf-btn-primary"
              onClick={handleNext}
              disabled={isSubmitting}
              style={{ marginLeft: 'auto' }}
            >
              {isSubmitting ? (
                <><RefreshCw size={16} className="cf-spin" /> Enviando...</>
              ) : isLast ? (
                <><Sparkles size={16} /> Finalizar</>
              ) : (
                <>Continuar <FiChevronRight /></>
              )}
            </button>
          </div>

          <p className="cf-personal-hint">
            {step + 1} de {PERSONAL_STEPS.length} — Pressione Enter para continuar
          </p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Pre-Analysis Review — runs detectEyeInImage on both captured images
// and shows per-eye validation results before allowing progression.
const PreAnalysisReview = ({ previewImages, onSubmit, onRecapture, isLoading }) => {
  const leftDone = previewImages.leftEye.length > 0;
  const rightDone = previewImages.rightEye.length > 0;

  const [validating, setValidating] = useState(true);
  const [eyeResult, setEyeResult] = useState({ right: null, left: null });
  // allow user to bypass a false-negative with a manual confirmation
  const [manualOverride, setManualOverride] = useState(false);

  // Run detection whenever images change (e.g. after re-capture)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setValidating(true);
      setManualOverride(false);
      const [right, left] = await Promise.all([
        previewImages.rightEye[0]
          ? detectEyeInImage(previewImages.rightEye[0].url)
          : Promise.resolve({ isValid: false, score: 0, details: {} }),
        previewImages.leftEye[0]
          ? detectEyeInImage(previewImages.leftEye[0].url)
          : Promise.resolve({ isValid: false, score: 0, details: {} }),
      ]);
      if (!cancelled) {
        setEyeResult({ right, left });
        setValidating(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [previewImages]);

  const rightOk = eyeResult.right?.isValid;
  const leftOk = eyeResult.left?.isValid;
  const allValid = rightOk && leftOk;
  const canProceed = (allValid || manualOverride) && !validating && leftDone && rightDone;

  // Helper: label + colour per validation state
  const eyeStatus = (done, result) => {
    if (!done) return { cls: 'missing', label: 'Pendente' };
    if (validating) return { cls: 'validating', label: 'Analisando…' };
    if (result?.isValid) return { cls: 'ok', label: `Íris detectada (${result.score}/100)` };
    return { cls: 'warn', label: `Não detectada (${result?.score ?? 0}/100) — verifique a imagem` };
  };

  const rightStatus = eyeStatus(rightDone, eyeResult.right);
  const leftStatus = eyeStatus(leftDone, eyeResult.left);

  return (
    <motion.div
      className="cf-review"
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="cf-review-header">
        <ScanLine size={28} className={validating ? 'cf-review-scan-icon' : 'cf-review-scan-icon done'} />
        <h2 className="cf-review-title">
          {validating ? 'Validando imagens…' : allValid ? 'Íris detectada com sucesso' : 'Verificação das imagens'}
        </h2>
        <p className="cf-review-subtitle">
          {validating
            ? 'Verificando se as imagens contêm uma íris…'
            : allValid
              ? 'Suas íris foram detectadas e estão prontas para análise'
              : 'Uma ou ambas as imagens não passaram na validação. Recapture ou confirme manualmente.'}
        </p>
      </div>

      {/* Per-eye validation cards */}
      <div className="cf-review-eye-cards">
        {/* Right eye */}
        <div className={`cf-review-eye-card ${rightStatus.cls}`}>
          <div className="cf-review-eye-card-header">
            <Eye size={16} />
            <span>Olho Direito</span>
            {!validating && !rightOk && rightDone && (
              <button className="cf-recapture-btn" onClick={() => onRecapture('right')}>
                <RefreshCw size={13} /> Recapturar
              </button>
            )}
          </div>
          {previewImages.rightEye[0] && (
            <div className="cf-review-eye-thumb-wrap">
              <img src={previewImages.rightEye[0].url} alt="Direito" className="cf-review-eye-thumb" />
              <div className={`cf-review-eye-badge ${rightStatus.cls}`}>
                {validating ? <RefreshCw size={11} className="cf-spin" /> : rightOk ? <CheckCircle size={11} /> : '⚠'}
                <span>{rightStatus.label}</span>
              </div>
            </div>
          )}
        </div>

        {/* Left eye */}
        <div className={`cf-review-eye-card ${leftStatus.cls}`}>
          <div className="cf-review-eye-card-header">
            <Eye size={16} />
            <span>Olho Esquerdo</span>
            {!validating && !leftOk && leftDone && (
              <button className="cf-recapture-btn" onClick={() => onRecapture('left')}>
                <RefreshCw size={13} /> Recapturar
              </button>
            )}
          </div>
          {previewImages.leftEye[0] && (
            <div className="cf-review-eye-thumb-wrap">
              <img src={previewImages.leftEye[0].url} alt="Esquerdo" className="cf-review-eye-thumb" />
              <div className={`cf-review-eye-badge ${leftStatus.cls}`}>
                {validating ? <RefreshCw size={11} className="cf-spin" /> : leftOk ? <CheckCircle size={11} /> : '⚠'}
                <span>{leftStatus.label}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Manual override — shown when at least one eye failed */}
      {!validating && !allValid && leftDone && rightDone && (
        <div className="cf-review-override">
          <label className="cf-override-label">
            <input
              type="checkbox"
              checked={manualOverride}
              onChange={e => setManualOverride(e.target.checked)}
            />
            <span>Tenho certeza que enviei fotos dos meus olhos e quero prosseguir mesmo assim</span>
          </label>
        </div>
      )}

      {/* Blurred report preview */}
      <div className="cf-review-blur-preview">
        <div className="cf-blur-overlay">
          <Star size={20} className="cf-blur-icon" />
          <span>Sua análise está sendo preparada…</span>
        </div>
        <div className="cf-blur-bars">
          {[80, 100, 70, 90, 60].map((w, i) => (
            <div key={i} className="cf-blur-bar" style={{ width: `${w}%`, animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
      </div>

      <button
        type="button"
        className="cf-btn-submit"
        onClick={onSubmit}
        disabled={isLoading || !canProceed}
      >
        {isLoading ? (
          <><RefreshCw size={18} className="cf-spin" /> Enviando para análise…</>
        ) : validating ? (
          <><RefreshCw size={18} className="cf-spin" /> Validando…</>
        ) : (
          <><ArrowRight size={18} /> Enviar para Análise de IA</>
        )}
      </button>

      <div className="cf-review-security">
        <ShieldCheck size={14} /> Seus dados são 100% seguros e privados
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const ClientForm = ({ onSubmit, isLoading }) => {
  const { t } = useTranslation();
  const { countryConfig } = useCountry();

  const [wizardStep, setWizardStep] = useState('onboarding');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraEye, setCameraEye] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    language: i18n.language,
    birthDate: null,
    phone: '',
    iris: {
      irisLeft: { images: [] },
      irisRight: { images: [] }
    }
  });

  const [previewImages, setPreviewImages] = useState({
    leftEye: [],
    rightEye: []
  });

  const openCamera = (eyeSide) => {
    setCameraEye(eyeSide);
    setShowCamera(true);
  };

  const closeCamera = () => {
    setShowCamera(false);
    setCameraEye(null);
  };

  const processFiles = useCallback(async (files, eyeSide) => {
    const irisKey = eyeSide === 'left' ? 'irisLeft' : 'irisRight';
    const previewKey = eyeSide === 'left' ? 'leftEye' : 'rightEye';

    if (formData.iris[irisKey].images.length + files.length > 1) {
      toast.error(`Máximo de 1 imagem por olho.`);
      return;
    }
    const invalidFiles = files.filter(f => !f.type.includes('image/'));
    if (invalidFiles.length > 0) { toast.error('Envie apenas arquivos de imagem.'); return; }

    const processedFiles = [];
    const newPreviews = [...previewImages[previewKey]];

    for (const file of files) {
      try {
        const resizedImage = await resizeImage(file);
        const newName = `eye-${eyeSide}-${formData.iris[irisKey].images.length + processedFiles.length}.JPEG`;
        const renamedFile = new File([resizedImage], newName, { type: resizedImage.type });
        processedFiles.push(renamedFile);
        newPreviews.push({ url: URL.createObjectURL(renamedFile), name: newName });
      } catch (error) {
        toast.error(`Erro ao processar imagem`);
      }
    }

    setFormData(prev => ({
      ...prev,
      iris: {
        ...prev.iris,
        [irisKey]: { ...prev.iris[irisKey], images: [...prev.iris[irisKey].images, ...processedFiles] }
      }
    }));
    setPreviewImages(prev => ({ ...prev, [previewKey]: newPreviews }));
  }, [formData, previewImages]);

  const handleCameraCapture = useCallback(async (file) => {
    await processFiles([file], cameraEye);
    closeCamera();
    // Auto-advance to next eye or validate step
    const otherEye = cameraEye === 'left' ? 'right' : 'left';
    const otherKey = otherEye === 'left' ? 'irisLeft' : 'irisRight';
    const currentEyeProcessed = true; // just captured
    const otherEyeDone = formData.iris[otherKey].images.length > 0;
    if (currentEyeProcessed && otherEyeDone) {
      setTimeout(() => setWizardStep('validate'), 600);
    }
  }, [cameraEye, processFiles, formData]);

  const handleUpload = (e, eyeSide) => {
    const files = Array.from(e.target.files);
    processFiles(files, eyeSide);
  };

  const removeImage = (eyeSide, index) => {
    const irisKey = eyeSide === 'left' ? 'irisLeft' : 'irisRight';
    const previewKey = eyeSide === 'left' ? 'leftEye' : 'rightEye';

    setFormData(prev => {
      const newImages = [...prev.iris[irisKey].images];
      newImages.splice(index, 1);
      return { ...prev, iris: { ...prev.iris, [irisKey]: { ...prev.iris[irisKey], images: newImages } } };
    });

    const newPreviews = [...previewImages[previewKey]];
    URL.revokeObjectURL(newPreviews[index].url);
    newPreviews.splice(index, 1);
    setPreviewImages(prev => ({ ...prev, [previewKey]: newPreviews }));
  };

  const handlePersonalFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canProceedToValidate = formData.iris.irisLeft.images.length > 0 && formData.iris.irisRight.images.length > 0;

  const handleFinalSubmit = () => {
    // Prevent duplicate submissions
    if (isSubmitting || isLoading) return;

    const { iris, ...rest } = formData;

    if (!rest.name?.trim()) { toast.error('Por favor, informe seu nome.'); return; }
    if (!rest.email?.trim() || !/\S+@\S+\.\S+/.test(rest.email)) { toast.error('Por favor, informe um e-mail válido.'); return; }
    if (!rest.birthDate?.trim() || rest.birthDate === 'null' || rest.birthDate.length < 10) { 
      toast.error('Por favor, informe sua data de nascimento.'); 
      return; 
    }
    if (!rest.phone?.trim()) { toast.error('Por favor, informe seu telefone.'); return; }
    if (iris.irisLeft.images.length === 0 || iris.irisRight.images.length === 0) {
      toast.error('Por favor, envie pelo menos uma imagem de cada olho.');
      return;
    }

    setIsSubmitting(true);
    
    const submitData = new FormData();
    submitData.append('name', rest.name);
    submitData.append('email', rest.email);
    submitData.append('language', rest.language);
    submitData.append('phone', rest.phone);
    submitData.append('birthDate', rest.birthDate);

    const irisData = {
      irisLeft: { images: [] },
      irisRight: { images: [] }
    };

    Array.from(iris.irisRight.images).forEach((image, index) => {
      if (typeof image === 'object') {
        irisData.irisRight.images[index] = image.name;
        submitData.append('files', image);
      } else {
        irisData.irisRight.images[index] = image;
      }
    });

    Array.from(iris.irisLeft.images).forEach((image, index) => {
      if (typeof image === 'object') {
        irisData.irisLeft.images[index] = image.name;
        submitData.append('files', image);
      } else {
        irisData.irisLeft.images[index] = image;
      }
    });

    submitData.append('iris', JSON.stringify(irisData));
    onSubmit(submitData);
    setIsSubmitting(false);
  };

  return (
    <div className="cf-container">
      <div className="cf-success-wrapper">
        {/* Wizard Stepper */}
        <WizardStepper currentStep={wizardStep} />

        {/* Step Content */}
        <AnimatePresence mode="wait">
          {wizardStep === 'onboarding' && (
            <motion.div key="onboarding" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <OnboardingStep onComplete={() => setWizardStep('capture')} />
            </motion.div>
          )}

          {wizardStep === 'capture' && (
            <motion.div
              key="capture"
              className="cf-capture-step"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <div className="cf-capture-header">
                <h2 className="cf-capture-title">
                  <ScanLine size={22} /> Captura da Íris
                </h2>
                <p className="cf-capture-subtitle">
                  Capture as íris de cada olho separadamente.
                </p>
              </div>

              {/* Gamified progress */}
              <div className="cf-capture-progress-badges">
                <div className={`cf-eye-badge ${formData.iris.irisRight.images.length > 0 ? 'done' : 'pending'}`}>
                  {formData.iris.irisRight.images.length > 0
                    ? <><CheckCircle size={14} /> Olho Direito ✓</>
                    : <><Eye size={14} /> Olho Direito</>}
                </div>
                <div className={`cf-eye-badge ${formData.iris.irisLeft.images.length > 0 ? 'done' : 'pending'}`}>
                  {formData.iris.irisLeft.images.length > 0
                    ? <><CheckCircle size={14} /> Olho Esquerdo ✓</>
                    : <><Eye size={14} /> Olho Esquerdo</>}
                </div>
              </div>

              <div className="cf-eyes-grid">
                <EyeCard
                  side="right"
                  label="Olho Direito"
                  image={previewImages.rightEye[0]}
                  onCameraOpen={() => openCamera('right')}
                  onUpload={(e) => handleUpload(e, 'right')}
                  onRemove={() => removeImage('right', 0)}
                  isLoading={isLoading}
                />
                <EyeCard
                  side="left"
                  label="Olho Esquerdo"
                  image={previewImages.leftEye[0]}
                  onCameraOpen={() => openCamera('left')}
                  onUpload={(e) => handleUpload(e, 'left')}
                  onRemove={() => removeImage('left', 0)}
                  isLoading={isLoading}
                />
              </div>

              {/* Celebration when both captured */}
              <AnimatePresence>
                {canProceedToValidate && (
                  <motion.div
                    className="cf-both-captured"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Sparkles size={20} /> Excelente! Ambas as íris capturadas com sucesso!
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="cf-capture-nav">
                <button className="cf-btn-ghost" onClick={() => setWizardStep('onboarding')}>
                  <FiChevronLeft /> Voltar
                </button>
                <button
                  className="cf-btn-primary"
                  onClick={() => setWizardStep('validate')}
                  disabled={!canProceedToValidate}
                >
                  Validar Capturas <FiChevronRight />
                </button>
              </div>
            </motion.div>
          )}

          {wizardStep === 'validate' && (
            <motion.div
              key="validate"
              className="cf-validate-step"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <PreAnalysisReview
                previewImages={previewImages}
                onSubmit={() => setWizardStep('personal')}
                onRecapture={(side) => {
                  // Remove the failed eye image and go back to capture step
                  removeImage(side, 0);
                  setWizardStep('capture');
                }}
                isLoading={false}
              />
              <div className="cf-capture-nav" style={{ marginTop: '1rem' }}>
                <button className="cf-btn-ghost" onClick={() => setWizardStep('capture')}>
                  <FiChevronLeft /> Voltar à captura
                </button>
              </div>
            </motion.div>
          )}

          {wizardStep === 'personal' && (
            <motion.div
              key="personal"
              className="cf-personal-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <div className="cf-personal-header-section">
                <Sparkles size={24} className="cf-personal-spark" />
                <h2>Perfeito. Agora personalize sua análise.</h2>
                <p>Precisamos de alguns dados para enviar seu relatório completo.</p>
              </div>
              <PersonalDataWizard
                formData={formData}
                onChange={handlePersonalFieldChange}
                onComplete={handleFinalSubmit}
                isSubmitting={isSubmitting}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Camera Modal */}
        <AnimatePresence>
          {showCamera && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <CameraCapture
                cameraEye={cameraEye}
                onCapture={handleCameraCapture}
                onClose={closeCamera}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Logo */}
        <div className="cf-logo-wrap">
          <img
            src="https://storage.googleapis.com/e-iris-businesses/621b8185e0b4ed2240ce0f4b/logo.png"
            alt="e-Iris Logo"
            className="cf-logo"
          />
        </div>

        {/* WhatsApp Support */}
        <div className="divBtnWhatsApp">
          <button
            className="btnWhatsApp"
            onClick={() => window.open(encodeURI('https://api.whatsapp.com/send/?phone=5541988113285&text=' + t('index.whatsappMessage')), '_Blank')}
          >
            <FaWhatsapp size="2.2em" color="#fff" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientForm;
